import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@/lib/api'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Loader2, X, ListFilter, Trash2, XCircle } from 'lucide-react'
import { toast } from 'sonner'

type QueueDrawerProps = {
  isOpen: boolean
  onClose: () => void
}

export function QueueDrawer({ isOpen, onClose }: QueueDrawerProps) {
  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuth()

  const { data, isFetching } = useQuery({
    queryKey: ['queue', 'updates'],
    queryFn: api.queue.updates,
    enabled: isAuthenticated,
    refetchInterval: (query) => {
      if (!isAuthenticated) return false
      const hasItems = query.state.data?.queue && query.state.data.queue.length > 0
      return hasItems ? 3000 : false
    },
    refetchOnWindowFocus: isAuthenticated,
  })

  const clearQueueMutation = useMutation({
    mutationFn: api.queue.clear,
    onSuccess: (result) => {
      toast.success(result.message)
      queryClient.invalidateQueries({ queryKey: ['queue', 'updates'] })
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to clear queue')
    },
  })

  const removeQueueItemMutation = useMutation({
    mutationFn: api.queue.removeItem,
    onSuccess: (result) => {
      toast.success(result.message)
      queryClient.invalidateQueries({ queryKey: ['queue', 'updates'] })
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to remove item from queue')
    },
  })

  return (
    <aside
      className={cn(
        'fixed top-0 right-0 z-40 h-full w-80 border-l border-border bg-card/95 backdrop-blur transition-transform duration-200 ease-in-out flex flex-col',
        isOpen ? 'translate-x-0' : 'translate-x-full'
      )}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center space-x-2 text-xs uppercase tracking-[0.25em] text-muted-foreground">
          <ListFilter className="h-4 w-4" />
          <h2 className="font-semibold">Queue</h2>
        </div>
        <div className="flex items-center space-x-1">
          {(data?.queue?.length ?? 0) > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" disabled={clearQueueMutation.isPending}>
                  {clearQueueMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear Queue</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to clear all items in your queue? This will mark {data?.queue.length} item(s) as completed and cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => clearQueueMutation.mutate()}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Clear Queue
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isFetching && (
          <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Updating
          </div>
        )}
        <div className="space-y-3 p-4">
          {data?.queue.length ? (
            data.queue.map((item) => (
              <div key={item.job_id} className="border border-border bg-card/70 p-3 space-y-1">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                      {item.model_name || item.job_type} · {item.status}
                    </div>
                    <p className="text-sm font-medium text-foreground line-clamp-2 mt-1">
                      {item.prompt_preview || '—'}
                    </p>
                    <div className="text-xs text-muted-foreground mt-1">
                      {new Date(item.created_at).toLocaleTimeString()}
                    </div>
                  </div>
                  <div className="ml-2 flex items-center space-x-1">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-destructive"
                          disabled={removeQueueItemMutation.isPending}
                        >
                          {removeQueueItemMutation.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <XCircle className="h-3 w-3" />
                          )}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove from Queue</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to remove this {item.model_name || item.job_type} job from the queue? This will mark it as completed and cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => removeQueueItemMutation.mutate(item.job_id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Remove
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">Queue is empty</p>
          )}
        </div>
      </div>

      <div className="border-t border-border px-4 py-3 text-xs text-muted-foreground space-y-1">
        <div>Updated {data ? new Date(data.server_time).toLocaleTimeString() : '—'}</div>
        {data && data.global_queue_count > 0 && (
          <div className="text-[11px]">
            {data.global_queue_count} other {data.global_queue_count === 1 ? 'generation' : 'generations'} in queue
          </div>
        )}
      </div>
    </aside>
  )
}
